// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import { useState } from "react";
import { SmartLink } from "@/components/SmartLink";
import type { BlockConfigOf } from "@/lib/blocks/schemas";

type Config = BlockConfigOf<"servicesTabs">;
type Item = Config["items"][number];

const BG_CLASSES: Record<Config["background"], string> = {
  "opichi-green-light": "bg-opichi-green-light",
  white: "bg-white",
  "stone-100": "bg-stone-100",
};

// Static class map so Tailwind's JIT can see every value. The previous
// `sm:grid-cols-${items.length}` interpolation silently dropped
// `sm:grid-cols-3` because template literals aren't scanned.
const TAB_GRID_COLS: Record<1 | 2 | 3, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-3",
};

/**
 * Interactive tab switcher. Pure client component — takes a fully-
 * resolved items array from its server parent (which may have built it
 * from inline config or the Markets table). All tab contents render
 * stacked in one grid cell so the card height is pinned to the tallest
 * panel — no layout jump on tab switch.
 */
export function ServicesTabsClient({
  label,
  background,
  items,
}: {
  label: string;
  background: Config["background"];
  items: Item[];
}) {
  const [active, setActive] = useState(0);
  if (items.length === 0) return null;
  const activeIdx = Math.min(active, items.length - 1);
  const tabCols = Math.min(items.length, 3) as 1 | 2 | 3;

  return (
    <section
      className={`w-full py-20 px-6 md:px-12 lg:px-24 ${BG_CLASSES[background]}`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-sm uppercase tracking-widest text-black/60 mb-8">
          [ {label} ]
        </div>

        <div className={`grid ${TAB_GRID_COLS[tabCols]} gap-4 md:gap-8 mb-12`}>
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              className={`service-tab brutal-border p-6 md:p-8 text-center cursor-pointer ${
                activeIdx === i ? "active" : "bg-white"
              }`}
            >
              <div className="font-[family-name:var(--font-display)] text-3xl md:text-5xl mb-2">
                {item.tabLabel}
              </div>
              {item.number && (
                <div className="text-xs uppercase tracking-widest opacity-60">
                  {item.number}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="brutal-border bg-white p-8 md:p-12">
          <div className="relative grid">
            {items.map((item, i) => (
              <div
                key={i}
                aria-hidden={i !== activeIdx}
                className={`col-start-1 row-start-1 transition-opacity duration-150 ${
                  i === activeIdx
                    ? "opacity-100"
                    : "invisible pointer-events-none opacity-0"
                }`}
              >
                <TabPanel item={item} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TabPanel({ item }: { item: Item }) {
  return (
    <div className="grid md:grid-cols-3 gap-8 items-start">
      <div className="md:col-span-2">
        <h3 className="font-[family-name:var(--font-display)] text-4xl md:text-5xl mb-6">
          {item.title}
        </h3>
        {item.description && (
          <p className="text-stone-700 leading-relaxed mb-6 whitespace-pre-line">
            {item.description}
          </p>
        )}
        {item.features.length > 0 && (
          <ul className="space-y-2 text-sm">
            {item.features.map((f, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-2 h-2 bg-black shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {item.number && (
          <div
            aria-hidden="true"
            data-num={item.number}
            className="ghost-numeral text-6xl font-[family-name:var(--font-display)] text-stone-200"
          />
        )}
        {item.cta && (
          <SmartLink
            href={item.cta.href}
            className="brutal-border-sm bg-black text-white px-6 py-3 uppercase tracking-widest text-sm hover:bg-stone-800 transition-colors text-center"
          >
            {item.cta.label}
          </SmartLink>
        )}
      </div>
    </div>
  );
}
